/** Alignment + distribution math for Tier 2 Group D's Align &
 *  Distribute panel. Pure functions — no DOM, no canvas, no
 *  Fabric. Caller supplies layer bboxes; we return per-layer
 *  `{dx, dy}` deltas to apply. The visual call (mutating Fabric
 *  object positions, marking dirty, snapshotting history) lives
 *  in the caller — typically EditorPage routed through a hook
 *  exported from `useFabricCanvas`.
 *
 *  Two operations:
 *    1. Alignment — move every layer in `layers` so a specified
 *       edge / center anchor (left / center-x / right / top /
 *       center-y / bottom) lands at a reference position. The
 *       reference comes from the union bbox of `layers` (when
 *       `target: 'selection'`) or the canvas (when
 *       `target: 'canvas'`). Per-layer dx OR dy is non-zero (not
 *       both) — alignment is single-axis.
 *    2. Distribution — re-space 3+ layers evenly along an axis.
 *       The first and last layers keep their positions; the
 *       middle layers shift so the GAPS between consecutive
 *       bboxes are equal. */

import type { LayerBBox } from './smart-snap';

export type AlignAnchor = 'left' | 'center-x' | 'right' | 'top' | 'center-y' | 'bottom';
export type AlignTarget = 'selection' | 'canvas';
export type DistributeAxis = 'horizontal' | 'vertical';

export interface AlignDelta {
  /** Original layer id from the input — caller uses to look up
   *  the Fabric object and apply the offset. */
  id: string;
  dx: number;
  dy: number;
}

export interface AlignOptions {
  layers: LayerBBox[];
  anchor: AlignAnchor;
  target: AlignTarget;
  /** Canvas dims — required when `target: 'canvas'`; ignored for
   *  `target: 'selection'`. */
  canvasWidth?: number;
  canvasHeight?: number;
}

/** Compute the reference X / Y coordinate the alignment anchor
 *  should land on. Centralized so the per-layer delta loop stays
 *  simple. */
function referenceForAnchor(opts: AlignOptions): number {
  const isXAxis = opts.anchor === 'left' || opts.anchor === 'center-x' || opts.anchor === 'right';
  if (opts.target === 'canvas') {
    const w = opts.canvasWidth ?? 0;
    const h = opts.canvasHeight ?? 0;
    if (opts.anchor === 'left') return 0;
    if (opts.anchor === 'right') return w;
    if (opts.anchor === 'center-x') return w / 2;
    if (opts.anchor === 'top') return 0;
    if (opts.anchor === 'bottom') return h;
    return h / 2; // center-y
  }
  // target === 'selection' — reference is the union bbox of all
  // input layers on the matching axis.
  let min = Infinity;
  let max = -Infinity;
  for (const l of opts.layers) {
    const lo = isXAxis ? l.left : l.top;
    const hi = isXAxis ? l.left + l.width : l.top + l.height;
    if (lo < min) min = lo;
    if (hi > max) max = hi;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return 0;
  if (opts.anchor === 'left' || opts.anchor === 'top') return min;
  if (opts.anchor === 'right' || opts.anchor === 'bottom') return max;
  return (min + max) / 2; // center-x / center-y
}

/** Compute the per-layer translation deltas needed to align
 *  `layers` to `anchor` against the chosen `target`. */
export function computeAlignDeltas(opts: AlignOptions): AlignDelta[] {
  if (opts.layers.length === 0) return [];
  const ref = referenceForAnchor(opts);
  return opts.layers.map((l) => {
    let dx = 0;
    let dy = 0;
    switch (opts.anchor) {
      case 'left':
        dx = ref - l.left;
        break;
      case 'center-x':
        dx = ref - (l.left + l.width / 2);
        break;
      case 'right':
        dx = ref - (l.left + l.width);
        break;
      case 'top':
        dy = ref - l.top;
        break;
      case 'center-y':
        dy = ref - (l.top + l.height / 2);
        break;
      case 'bottom':
        dy = ref - (l.top + l.height);
        break;
    }
    return { id: l.id, dx, dy };
  });
}

/** Compute per-layer translation deltas needed to evenly
 *  distribute 3+ layers along the chosen axis. The leftmost +
 *  rightmost (or topmost + bottommost) layers keep their
 *  positions; everything in between shifts so the GAPS between
 *  consecutive bboxes are equal.
 *
 *  Returns an empty array for fewer than 3 layers (distribution
 *  is meaningless for 2 — there's no "middle" to redistribute). */
export function computeDistributeDeltas(layers: LayerBBox[], axis: DistributeAxis): AlignDelta[] {
  if (layers.length < 3) return [];

  // Sort by leading edge on the distribution axis.
  const sorted = [...layers].sort((a, b) => {
    return axis === 'horizontal' ? a.left - b.left : a.top - b.top;
  });

  // Available gap space = (last layer's trailing edge) - (first
  // layer's leading edge) - (sum of bbox sizes on this axis).
  // Distributed gap = available / (n - 1).
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  let totalSize = 0;
  for (const l of sorted) totalSize += axis === 'horizontal' ? l.width : l.height;
  const span =
    axis === 'horizontal'
      ? last.left + last.width - first.left
      : last.top + last.height - first.top;
  const gap = (span - totalSize) / (sorted.length - 1);

  // Compute each layer's target leading edge directly from its
  // index rather than accumulating a cursor — avoids IEEE 754
  // drift on irrational gaps (e.g. four-layer distribution with
  // gap 200/3) that would land the last layer at
  // `0 + 5.68e-14` instead of `0`.
  const start = axis === 'horizontal' ? first.left : first.top;
  const sizes: number[] = sorted.map((l) => (axis === 'horizontal' ? l.width : l.height));
  // Cumulative sum of preceding-bbox sizes for each index.
  const prefixSize: number[] = [0];
  for (let i = 0; i < sizes.length - 1; i++) prefixSize.push(prefixSize[i] + sizes[i]);

  const out: AlignDelta[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const l = sorted[i];
    const targetEdge = start + prefixSize[i] + gap * i;
    const currentEdge = axis === 'horizontal' ? l.left : l.top;
    const delta = targetEdge - currentEdge;
    if (axis === 'horizontal') {
      out.push({ id: l.id, dx: delta, dy: 0 });
    } else {
      out.push({ id: l.id, dx: 0, dy: delta });
    }
  }
  return out;
}
